import {
    Badge,
    classNames,
    CodeDigits,
    KeyPad,
    layout,
    PushButton,
    TextInput,
    theme,
    typography,
    useAsyncCallback,
    useBarcodeListener,
    useCardListener,
    useIdle,
    usePlatform,
    useStorageItemBlob, useStorageItemValue,
    useTypeRef,
    View,
    ViewContent,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {Barcode, LogLevel, NfcCard} from "@variocube/app-api";
import {useLocalization} from "../i18n";
import {Tenant} from "./types";

export interface HomeViewProps {
    onCode: (code: string) => Promise<void>;
}

export function HomeView({onCode}: HomeViewProps) {
    const platform = usePlatform();
    const {t} = useLocalization();

    const [code, setCode] = useState("");
    const backgroundImageBlob = useStorageItemBlob("backgroundImage");
    const tenant = useStorageItemValue<Tenant>("tenant");
    const backgroundImageUrl = useMemo(() => backgroundImageBlob && URL.createObjectURL(backgroundImageBlob), [backgroundImageBlob]);
    const customBackground = backgroundImageUrl && style({backgroundImage: `url("${backgroundImageUrl}")`});
    const [type, setType] = useTypeRef();

    const {loading, error, execute} = useAsyncCallback(async () => {
        if(code) {
            return onCode(code);
        }
    }, [onCode, code]);

    useEffect(() => {
        if(error) {
            setCode("")
        }
    }, [error])

    const idle = useIdle(15);

    useEffect(() => {
        if (idle) {
            setCode("");
            execute();
        }
    }, [idle, execute]);

    const handleBarcode = useCallback(async ({code}: Barcode) => {
        try {
            execute();
            await platform.log(LogLevel.Trace, "Barcode scanned", {code});
            await onCode(atob(code));
        } catch (error) {
            console.error("Error processing barcode");
        }
    }, [platform]);

    const handleCard = useCallback(async (nfcCard?:  NfcCard) => {
        let key = nfcCard?.uid ?? "";
        try {
            execute();
            await platform.log(LogLevel.Trace, "NFC  Key detected", {key});
            await onCode(key);
        } catch (error) {
            console.error("Error processing NFC");
        }
    }, [platform]);

    useBarcodeListener(handleBarcode);
    useCardListener(handleCard);

    return (
        <View>
            <ViewHeader className={theme.lessContrast}>
                <ViewHeaderItem className={classNames(layout.paddingMedium, styles.branding)}>
                    {tenant ? tenant.name : ""}
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={classNames(layout.vertical, layout.center, layout.alignCenter, customBackground)}>
                <div className={classNames(layout.horizontal)}>
                    <div className={classNames(theme.moreContrast, styles.boxLeft)}>
                        <TextInput
                            type={"password"}
                            onChange={setCode}
                            value={code}
                            disabled={loading}
                            typeRef={setType}
                        />
                        <KeyPad onType={type} digits={CodeDigits} disabled={loading}/>
                    </div>
                    <div
                        className={classNames(theme.lessContrast, layout.vertical, layout.spaceBetween, styles.boxRight)}>
                        <div>
                            <div className={classNames(typography.title, layout.marginYSmall)}>
                                {t("EnterAccessCode")}
                            </div>
                            <div className={typography.description}>
                                {t("EnterCodeMessage")}
                            </div>
                        </div>

                        <div>
                            {loading && (
                                <p>{t("CodeChecking")}</p>
                            )}
                            {!idle && error && (
                                <p>
                                    <Badge error>
                                        {t("AccessCodeInvalid")}
                                    </Badge>
                                </p>
                            )}
                        </div>

                        <div>
                            <PushButton primary className={classNames(styles.button, styles.marginBottom)}
                                        onClick={execute}>
                                {t("Submit")}
                            </PushButton>
                        </div>
                    </div>
                </div>
            </ViewContent>
        </View>
    )
}

const styles = stylesheet({
    branding: {
        fontSize: "2em",
        fontWeight: 300,
        color: "var(--color-primary)"
    },
    boxLeft: {
        width: "24em"
    },
    boxRight: {
        width: "20em",
        padding: "1.5em"
    },
    button: {
        width: "100%"
    },
    marginBottom: {
        marginBottom: "0.5rem"
    },
});
